import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';

/**
 * A yes/no question, in the dashboard's own dialog rather than the browser's.
 *
 * The browser's confirm box says "127.0.0.1 says", cannot be styled, blocks
 * the whole tab, and looks nothing like the product. There was no shared
 * confirm here before — every screen that needed one built its own — so this
 * is the general one.
 */

export interface ConfirmActionData {
   /** The question, in plain words. */
   message: string;
   /** Extra line for a consequence worth spelling out. */
   detail?: string;
   /** Defaults to "Confirm". */
   confirmLabel?: string;
   /** Defaults to "Cancel". */
   cancelLabel?: string;
   /** Red button, for anything that destroys something. */
   danger?: boolean;
   onConfirm: () => void;
}

interface Props {
   data: ConfirmActionData;
}

const ConfirmAction = ({ data }: Props) => {
   const { handleClose } = useUIStore();

   return (
      <Body>
         <Message>{data.message}</Message>
         {data.detail && <Detail>{data.detail}</Detail>}

         <Actions>
            <Btn
               title={data.cancelLabel ?? 'Cancel'}
               type="outline"
               size="medium"
               textcolor={colors.GRAY_DARK}
               onClick={handleClose}
            />
            <Btn
               title={data.confirmLabel ?? 'Confirm'}
               type="normal"
               size="medium"
               bgcolor={data.danger ? '#b8342a' : colors.PRIMARY}
               textcolor={colors.WHITE}
               onClick={() => {
                  data.onConfirm();
                  handleClose();
               }}
            />
         </Actions>
      </Body>
   );
};

export default ConfirmAction;

const Body = styled.div`
   width: min(420px, 76vw);
   padding-top: 4px;

   @media (max-width: 768px) {
      width: 100%;
      padding-bottom: 1rem;
   }
`;

const Message = styled.p`
   margin: 0;
   font-size: 14px;
   line-height: 1.55;
   color: ${colors.GRAY_DARK};
`;

const Detail = styled.p`
   margin: 8px 0 0;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.GRAY3};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 20px;
`;
